import React, { useCallback, useEffect, useRef, useState } from 'react';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import DepartmentCard from './subcomponents/DepartmentCard';
import Button from 'components/CustomButtons/Button';
import DownloadMenu from 'components/DownloadMenu/DownloadMenu';
import AddNumbersDialog from '../common/AddNumbersDialog';
import CanPerform from 'components/CanPerform/CanPerform';
import Add from '@material-ui/icons/Add';
import { translate } from 'components/Translation';
import { userTypeSelector } from 'redux/modules/session/selectors';
import _ from 'lodash';
import { exportToXLS } from 'utils/export/export';
import { DepartmentsService } from 'services/departments/departments';
import { AccountsService } from 'services/accounts/accounts';
import invalidateCache from 'utils/api/invalidateCache';
import { setToast } from 'redux/modules/toast/actions';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch, useSelector } from 'react-redux';
import { EMPTY, throwError } from 'rxjs';
import { concatMap, finalize } from 'rxjs/operators';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { SingleUser } from 'utils/api/models/users/singleUser';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import NoDepartments from './subcomponents/NoDepartments';
import AddDepartmentDialog from './subcomponents/AddDepartmentDialog';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';

interface IDepartmentsListProps {
	match: {
		params: {
			accountId: string;
		};
	};
	account: SingleCorpAccount;
}

const DepartmentsList = (props: IDepartmentsListProps) => {
	const [departments, setDepartments] = useState<SingleDepartment[]>([]);
	const [departmentDialog, setDepartmentDialog] = useState<boolean>(false);
	const [selectedDeptId, setSelectedDeptId] = useState<string>('');
	const [loadingDepartments, setLoadingDepartments] = useState<boolean>(true);
	const [addNumbersDialog, setAddNumbersDialog] = useState<boolean>(false);
	const [accountManagers, setAccountManagers] = useState<SingleUser[]>([]);
	const [loadingManagers, setLoadingManagers] = useState<boolean>(true);
	const dispatch = useDispatch();
	const userType = useSelector(userTypeSelector);
	const { accountId } = props.match.params;
	const history = useHistory();
	const isMounted = useRef(true);

	const fetchDepartments = useCallback(() => {
		setLoadingDepartments(true);
		dispatch(showLoadingIndicator());
		DepartmentsService.getDepartmentsForCurrentUser()
			.pipe(finalize(() => isMounted.current && setLoadingDepartments(false)))
			.subscribe(({ departments }) => {
				if (isMounted.current) {
					const filteredDepartments = departments.filter(
						(department) =>
							String(department.accountId) === accountId &&
							(department.deptId || (!department.deptId && department.numbersCount > 0))
					);
					setDepartments(filteredDepartments);
				}
			});
	}, [dispatch, accountId]);

	const numbersAdded = () => {
		fetchDepartments();
	};

	useEffect(() => {
		fetchDepartments();
	}, [fetchDepartments]);

	useEffect(() => {
		setLoadingManagers(true);
		dispatch(showLoadingIndicator());
		AccountsService.getCorpAccountManagers(accountId)
			.pipe(finalize(() => isMounted.current && setLoadingManagers(false)))
			.subscribe(({ managers }) => isMounted.current && setAccountManagers(managers));
	}, [accountId, dispatch]);

	useEffect(() => {
		if (!loadingDepartments && !loadingManagers) {
			dispatch(hideLoadingIndicator());
		}
	}, [loadingDepartments, loadingManagers, dispatch]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const departmentEnabled = (department: SingleDepartment) => {
		return (
			userType !== 'MANAGER' ||
			(userType === 'MANAGER' && (department.numbersCount > 0 || department.usersCount > 0))
		);
	};

	const showDetails = (department: SingleDepartment) => {
		if (departmentEnabled(department)) {
			let url = 'departments/details';
			if (department.deptId) {
				url += `/${department.deptId}`;
			}
			history.push(url);
		}
	};

	const createDepartment = (name: string, uiColor: string, selectedManagers: string[]) => {
		setDepartmentDialog(false);
		dispatch(showLoadingIndicator());
		let department: SingleDepartment;
		const addManagersResponse = DepartmentsService.addDepartment({
			name,
			uiColor,
			accountId,
			description: '',
		})
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
				})
			)
			.pipe(
				concatMap(({ response }) => {
					department = response.createDepartmentResponse.department;
					if (department && selectedManagers.length > 0) {
						return DepartmentsService.assignManagersToDepartment({
							departmentId: String(department.deptId),
							userIds: selectedManagers,
							permissionExpiration: '',
						});
					}
					addDepartment(department);
					return EMPTY;
				})
			);

		addManagersResponse
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
				})
			)
			.subscribe(
				({ response }) => {
					if (response.arguments.returnValue) {
						addDepartment(department, selectedManagers);
					} else {
						addDepartment(department);
					}
				},
				(error) => throwError(error)
			);
	};

	const addDepartment = (department: SingleDepartment, selectedManagers?: string[]) => {
		if (department) {
			const updatedDepartment = { ...department };
			invalidateCache(['getDepartmentsForCurrentUser'], true);
			if (selectedManagers) {
				const _managers = accountManagers
					.filter((manager: SingleUser) => selectedManagers.indexOf(manager.userId) >= 0)
					.map((manager: SingleUser) => manager.userName);
				if (_managers) {
					updatedDepartment.managers = _managers;
				}
			}
			setDepartments((departments) => [...departments, updatedDepartment]);
			dispatch(
				setToast({
					error: false,
					label: translate('accounts.department.added'),
					actionType: translate('added'),
				})
			);
		}
	};

	const exportToXLSX = () => {
		const data = departments.map((department) => ({
			'Department name': department.name,
			'Numbers count': department.numbersCount,
			'Users count': department.usersCount,
			Managers: _.toString(department.managers),
		}));

		exportToXLS(data, `departments ${moment().format('LLL')}`);
	};

	return (
		<div>
			<ConditionalRender
				show={departments.length > 0}
				onTrue={() => (
					<GridContainer justify="flex-start">
						<GridItem xs={4} container alignItems="center">
							<DownloadMenu
								items={[
									{
										name: 'XLSX',
										onClick: exportToXLSX,
									},
								]}
							/>
						</GridItem>
						<GridItem xs={8} container justify="flex-end" alignItems="center">
							<CanPerform
								action="departments:add-department"
								yes={
									<Button
										color="tertiary-light"
										id="add-department-button"
										withIcon
										onClick={() => setDepartmentDialog(true)}
									>
										<>
											<Add />
											{translate('departments.add.department')}
										</>
									</Button>
								}
							/>
						</GridItem>
						<>
							{(departments || []).map((department) => (
								<GridItem
									xs={12}
									sm={6}
									md={4}
									key={`${department.deptId || ''}${department.accountId}`}
								>
									<DepartmentCard
										department={department.name}
										uiColor={department.uiColor}
										managers={department.managers as string[]}
										details={{
											numbers: department.numbersCount,
											users: department.usersCount,
										}}
										onClick={() => showDetails(department)}
										addNumbers={() => {
											setSelectedDeptId(String(department.deptId));
											setAddNumbersDialog(true);
										}}
										notAssignedCard={!department.deptId}
										disabled={!departmentEnabled(department)}
									/>
								</GridItem>
							))}
						</>
					</GridContainer>
				)}
			/>
			<ConditionalRender
				show={!loadingDepartments && departments.length <= 0}
				onTrue={() => <NoDepartments showNewDepartmentDialog={() => setDepartmentDialog(true)} />}
			/>
			<AddDepartmentDialog
				isOpen={departmentDialog}
				hideDialog={() => setDepartmentDialog(false)}
				managers={accountManagers}
				createDepartment={createDepartment}
			/>
			<CanPerform
				action="departments:add-numbers"
				yes={
					<AddNumbersDialog
						isDialogOpen={addNumbersDialog}
						setIsDialogOpen={(value) => setAddNumbersDialog(value)}
						selectedDepartmentId={selectedDeptId}
						accountId={accountId}
						departments={departments}
						numbersAdded={numbersAdded}
					/>
				}
			/>
		</div>
	);
};

export default DepartmentsList;
